import React, { useState, useEffect } from 'react';
import TreatmentsListRouter from './treatments/treatmentList/treatmentListRouter.js';
import { Card, Alert, Tabs, Tab, Box, Menu, MenuItem } from '@mui/material';
import { useClinic } from '../../hooks/useClinic';
import { useParams } from 'react-router-dom';
import GeneralDataRouter from './generalData/generalDataRouter.js';
import GeneralEvolutionListRouter from './generalEvolution/generalEvolutionList/generalEvolutionListRouter.js';
import AnamnesisFormRouter from './anamnesis/anamnesisFormRouter.js';
import PrescriptionListRouter from './prescriptions/prescriptionList/prescriptionListRouter.js';
import AttestedListRouter from './attested/attestedList/attestedListRouter.js';
import ImageListRouter from './image/imageList/imageListRouter.js';
import TermsTabList from './terms/termsTabList.js';
import DocumentListRouter from './documents/documentList/documentListRouter.js';
import FinancialListWeb from './financial/financialListWeb.js';
import AestheticProtocolRouter from './aestheticProtocol/aestheticProtocolRouter.js';
import AppointmentListRouter from './appointments/appointmentList/appointmentListRouter.js';

const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowWidth;
};

const TAB_CONFIGS = [
  { key: 'general-data', label: 'Dados Gerais', component: GeneralDataRouter, isToRender: true },
  { key: 'anamnesis', label: 'Anamnese', component: AnamnesisFormRouter, isToRender: true },
  { key: 'treatments', label: 'Tratamentos', component: TreatmentsListRouter, isToRender: true },
  {
    key: 'aesthetic-protocol',
    label: 'Protocolos Estéticos',
    component: AestheticProtocolRouter,
    isToRender: true,
  },
  {
    key: 'prescriptions',
    label: 'Receituário',
    component: PrescriptionListRouter,
    isToRender: true,
  },
  { key: 'attested', label: 'Atestados', component: AttestedListRouter, isToRender: true },
  {
    key: 'evolutions',
    label: 'Evoluções',
    component: GeneralEvolutionListRouter,
    isToRender: true,
  },
  { key: 'terms', label: 'Termos', component: TermsTabList, isToRender: false },
  { key: 'images', label: 'Imagens e Anexos', component: ImageListRouter, isToRender: false },
  { key: 'documents', label: 'Documentos', component: DocumentListRouter, isToRender: false },
  { key: 'financial', label: 'Financeiro', component: FinancialListWeb, isToRender: false },
  {
    key: 'appointments',
    label: 'Consultas',
    component: AppointmentListRouter,
    isToRender: false,
  },
];

export default function PatientForm() {
  const { patientId } = useParams();
  const [currentTabKey, setCurrentTabKey] = useState('general-data');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const [selectedPatient, setSelectedPatient] = useState(null);

  const width = useWindowWidth();
  const isMobile = width < 768;

  const isNewPatient = !patientId;

  const [modules, setModules] = useState([]);

  const isAestheticOnly = modules.includes('AESTHETIC') && !modules.includes('ODONTO');

  const hasAestheticModule = modules.includes('AESTHETIC');

  const VISIBLE_TABS = TAB_CONFIGS.filter((t) => {
    if (!t.isToRender) return false;
    if (t.key === 'aesthetic-protocol' && !hasAestheticModule) return false;
    if (isAestheticOnly && t.key === 'attested') return false;
    if (isAestheticOnly && t.key === 'treatments') return false;
    return true;
  });

  const getTabLabel = (tab) => {
    if (tab.key === 'aesthetic-protocol') {
      return isAestheticOnly ? 'Protocolos Estéticos' : 'P. Estéticos';
    }
    return tab.label;
  };

  const changeActiveTab = (event, newKey) => {
    if (newKey != 'more-options') {
      setCurrentTabKey(newKey);
    }
  };

  const displaySaveConfirmation = (message) => {
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setSelectedPatient(null);
  };

  const activeTabConfig = TAB_CONFIGS.find((t) => t.key === currentTabKey) ?? TAB_CONFIGS[0];

  const ActiveTabComponent = activeTabConfig.component;

  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const moreOpen = Boolean(moreAnchorEl);

  const openMoreMenu = (event) => setMoreAnchorEl(event.currentTarget);
  const closeMoreMenu = () => setMoreAnchorEl(null);

  const { clinic } = useClinic();

  useEffect(() => {
    setModules(clinic?.modules ?? []);
  }, [clinic]);

  return (
    <Card sx={{ width: '100%' }}>
      {showSuccessMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      <Tabs
        value={currentTabKey}
        onChange={changeActiveTab}
        variant={isMobile ? 'scrollable' : 'fullWidth'}
        scrollButtons={isMobile ? true : false}
        allowScrollButtonsMobile
        sx={{
          width: '100%',
          '& .MuiTabs-flexContainer': {
            justifyContent: isMobile ? 'flex-start' : 'space-between',
          },
          '& .MuiTabs-scrollButtons': {
            width: 28,
            '&.Mui-disabled': { opacity: 0.3 },
          },
        }}
      >
        {VISIBLE_TABS.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            label={getTabLabel(tab)}
            disabled={isNewPatient && tab.key !== 'general-data'}
            sx={{
              fontSize: '0.8rem',
              paddingY: 1,
              whiteSpace: 'nowrap',
              minWidth: 'auto',
            }}
          />
        ))}
        <Tab
          key="more-options"
          value="more-options"
          label="Mais Opções"
          onClick={openMoreMenu}
          aria-haspopup="true"
          aria-controls={moreOpen ? 'more-options-menu' : undefined}
          aria-expanded={moreOpen ? 'true' : undefined}
          sx={{
            fontSize: '0.8rem',
            paddingY: 1,
            whiteSpace: 'nowrap',
            minWidth: 'auto',
          }}
        />
      </Tabs>

      <Menu
        id="more-options-menu"
        anchorEl={moreAnchorEl}
        open={moreOpen}
        onClose={closeMoreMenu}
        slotProps={{ list: { 'aria-label': 'Mais opções do paciente' } }}
      >
        <MenuItem
          onClick={() => {
            setCurrentTabKey('images');
            closeMoreMenu();
          }}
        >
          Imagens e Anexos
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCurrentTabKey('documents');
            closeMoreMenu();
          }}
        >
          Documentos
        </MenuItem>
        <MenuItem
          onClick={() => {
            setCurrentTabKey('terms');
            closeMoreMenu();
          }}
        >
          Termos
        </MenuItem>

        <MenuItem
          onClick={() => {
            setCurrentTabKey('financial');
            closeMoreMenu();
          }}
        >
          Financeiro
        </MenuItem>

        <MenuItem
          onClick={() => {
            setCurrentTabKey('appointments');
            closeMoreMenu();
          }}
        >
          Consultas
        </MenuItem>
      </Menu>

      <Box sx={{ p: isMobile ? 1 : 2 }}>
        <ActiveTabComponent
          patient={selectedPatient}
          patientId={patientId}
          onSaveSuccess={displaySaveConfirmation}
        />
      </Box>
    </Card>
  );
}
